import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInstagram } from '@fortawesome/free-brands-svg-icons';
import { faUpload, faTrashCan, faDownload, faCircleExclamation, faImage, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import './SocialPhoto.css';

// the post maker (templates, fonts, background removal) loads only when opened
const PostMaker = lazy(() => import('../components/PostMaker/PostMaker'));

/* =========================================================
   Admin › student profile › Overview › "Photo for social media".
   One photo per student (the one the parent sends on WhatsApp), kept apart from their profile
   picture and reused for every Instagram post. Admin only; the file is not public.
   Backend: /api/admin/studentProfile/social-photo (backend/api/Admin/SocialPhoto.js)
   ========================================================= */
const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });
const BASE = `${API}/api/admin/studentProfile/social-photo`;

const dayOf = (v) => {
  const d = v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime())
    ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'Asia/Beirut' })
    : '';
};

const SocialPhoto = ({ studentId, name }) => {
  const [info, setInfo] = useState(null); // { has, at }
  const [src, setSrc] = useState(null); // blob url of the photo
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [making, setMaking] = useState(false);
  const inputRef = useRef(null);
  const srcRef = useRef(null);

  const setBlob = (blob) => {
    if (srcRef.current) URL.revokeObjectURL(srcRef.current);
    srcRef.current = blob ? URL.createObjectURL(blob) : null;
    setSrc(srcRef.current);
  };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${BASE}/${studentId}`, auth());
      setInfo(r.data);
      if (r.data?.has) {
        const f = await axios.get(`${BASE}/${studentId}/file`, { ...auth(), responseType: 'blob' });
        setBlob(f.data);
      } else {
        setBlob(null);
      }
    } catch {
      setInfo({ has: false, failed: true });
    }
  }, [studentId]);

  useEffect(() => {
    load();
    return () => {
      if (srcRef.current) URL.revokeObjectURL(srcRef.current);
    };
  }, [load]);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\//.test(file.type)) return setError('Choose a photo (JPG or PNG).');
    if (file.size > 15 * 1024 * 1024) return setError('This photo is too large (max 15MB).');
    setError('');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      await axios.post(`${BASE}/${studentId}`, fd, auth());
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Couldn’t save the photo. Try again.');
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  const remove = async () => {
    if (!window.confirm(`Remove the social media photo of ${name}?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${BASE}/${studentId}`, auth());
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'Couldn’t remove the photo.');
    } finally {
      setBusy(false);
    }
  };

  if (!info) return null;
  if (info.failed) return null; // e.g. backend not updated yet

  const fileName = `${String(name || 'student').trim().replace(/\s+/g, '-')}-social.jpg`;

  return (
    <section className="rmsocp" aria-labelledby={`rmsocp-title-${studentId}`}>
      <div className="rmsocp-head">
        <h3 id={`rmsocp-title-${studentId}`}>
          <FontAwesomeIcon icon={faInstagram} /> Photo for social media
        </h3>
        <p>Used for the “Meet our new coder” and “Course completed” posts. Not their profile picture, and not shown to anyone else.</p>
      </div>

      <div className="rmsocp-body">
        <div className={`rmsocp-pic ${src ? '' : 'is-empty'}`}>
          {src ? <img src={src} alt={`${name}, photo for social media`} /> : <FontAwesomeIcon icon={faImage} />}
        </div>

        <div className="rmsocp-side">
          {info.has ? (
            <small>Added {dayOf(info.at)}</small>
          ) : (
            <small>No photo yet. Use “Ask for photo” to ask the parents on WhatsApp, then add it here.</small>
          )}
          <div className="rmsocp-actions">
            <button type="button" className="rmsocp-btn rmsocp-btn--make" onClick={() => setMaking(true)}>
              <FontAwesomeIcon icon={faWandMagicSparkles} /> Create Instagram post
            </button>
            <input ref={inputRef} type="file" accept="image/*" hidden onChange={pick} />
            <button type="button" className="rmsocp-btn" disabled={busy} onClick={() => inputRef.current?.click()}>
              <FontAwesomeIcon icon={faUpload} /> {busy ? 'Saving…' : info.has ? 'Replace photo' : 'Add photo'}
            </button>
            {src && (
              <a className="rmsocp-btn rmsocp-btn--ghost" href={src} download={fileName}>
                <FontAwesomeIcon icon={faDownload} /> Download
              </a>
            )}
            {info.has && (
              <button type="button" className="rmsocp-btn rmsocp-btn--ghost rmsocp-btn--danger" disabled={busy} onClick={remove}>
                <FontAwesomeIcon icon={faTrashCan} /> Remove
              </button>
            )}
          </div>
          {error && (
            <p className="rmsocp-error" role="alert">
              <FontAwesomeIcon icon={faCircleExclamation} /> {error}
            </p>
          )}
        </div>
      </div>
      {making && (
        <Suspense fallback={null}>
          <PostMaker studentId={studentId} onClose={() => setMaking(false)} />
        </Suspense>
      )}
    </section>
  );
};

export default SocialPhoto;
